import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Search, Filter, Eye, Edit2, Trash2, Download, FileText as FileTextIcon, FileSpreadsheet } from 'lucide-react';
import api from '@/utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/toast';

const Leads = () => {
  const { hasPermission, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLoanType, setFilterLoanType] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all'); // mine | team | all
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const canCreate = hasPermission('create_leads');
  const canEdit = hasPermission('edit_leads');
  const canDelete = hasPermission('delete_leads');
  const canExport = hasPermission('export_leads');

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterLoanType) params.loan_type = filterLoanType;
      if (searchTerm) params.search = searchTerm;
      if (scopeFilter && scopeFilter !== 'all') params.scope = scopeFilter;
      const response = await api.get('/leads', { params });
      let rows = response.data;
      // Frontend safety net for "mine" — backend is authoritative
      if (scopeFilter === 'mine') {
        rows = rows.filter((l) => l.created_by === user?.id || l.current_owner === user?.id || l.assigned_to === user?.id);
      }
      setLeads(rows);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLoanType, searchTerm, scopeFilter, user?.id]);

  useEffect(() => {
    fetchLeads();
    const action = searchParams.get('action');
    if (action === 'new') {
      navigate('/leads/new');
    }
  }, [fetchLeads, searchParams, navigate]);

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await api.delete(`/leads/${leadId}`);
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      alert('Failed to delete lead');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterLoanType) params.loan_type = filterLoanType;
      if (searchTerm) params.search = searchTerm;
      const response = await api.get(`/leads/export/${format}`, {
        params,
        responseType: 'blob',
      });
      const ext = format === 'excel' ? 'xlsx' : format;
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `goldpeak-leads.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess(`Exported ${format.toUpperCase()} successfully`);
    } catch (error) {
      showError(error.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.mobile_number.includes(searchTerm) ||
    (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      fresh: 'bg-blue-100 text-blue-800',
      contacted: 'bg-indigo-100 text-indigo-800',
      interested: 'bg-cyan-100 text-cyan-800',
      documents_pending: 'bg-yellow-100 text-yellow-800',
      follow_up: 'bg-orange-100 text-orange-800',
      login: 'bg-purple-100 text-purple-800',
      login_done: 'bg-purple-100 text-purple-800',
      under_process: 'bg-violet-100 text-violet-800',
      sanctioned: 'bg-green-100 text-green-800',
      disbursed: 'bg-emerald-100 text-emerald-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return statusConfig[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Leads</h1>
            <p className="text-muted-foreground">Manage your customer leads</p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-2 flex-wrap">
            {canExport && (
              <div className="relative">
                <button
                  data-testid="export-leads-button"
                  onClick={() => setShowExportMenu((s) => !s)}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-3 border border-border bg-card hover:bg-muted font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <Download size={18} />
                  {exporting ? 'Exporting…' : 'Export'}
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-card border border-border rounded-lg shadow-lg z-20" onMouseLeave={() => setShowExportMenu(false)}>
                    <button
                      data-testid="export-csv"
                      onClick={() => handleExport('csv')}
                      className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm"
                    >
                      <FileTextIcon size={16} /> CSV
                    </button>
                    <button
                      data-testid="export-excel"
                      onClick={() => handleExport('excel')}
                      className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm"
                    >
                      <FileSpreadsheet size={16} /> Excel
                    </button>
                    <button
                      data-testid="export-pdf"
                      onClick={() => handleExport('pdf')}
                      className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm"
                    >
                      <FileTextIcon size={16} /> PDF
                    </button>
                  </div>
                )}
              </div>
            )}
            {canCreate && (
              <button
                data-testid="add-lead-button"
                onClick={() => navigate('/leads/new')}
                className="flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors"
              >
                <Plus size={20} />
                Add New Lead
              </button>
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <input
                type="text"
                data-testid="search-leads"
                placeholder="Search by name, mobile, email, or co-applicant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <select
                data-testid="filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              >
                <option value="">All Status</option>
                <option value="fresh">Fresh Lead</option>
                <option value="contacted">Contacted</option>
                <option value="interested">Interested</option>
                <option value="documents_pending">Documents Pending</option>
                <option value="follow_up">Follow Up</option>
                <option value="login_done">Login Done</option>
                <option value="under_process">Under Process</option>
                <option value="sanctioned">Sanctioned</option>
                <option value="disbursed">Disbursed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="relative">
              <select
                data-testid="filter-loan-type"
                value={filterLoanType}
                onChange={(e) => setFilterLoanType(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              >
                <option value="">All Loan Types</option>
                <option value="Personal Loan">Personal Loan</option>
                <option value="Business Loan">Business Loan</option>
                <option value="Home Loan">Home Loan</option>
                <option value="Auto Loan">Auto Loan</option>
                <option value="Car Loan">Car Loan</option>
                <option value="Education Loan">Education Loan</option>
                <option value="Loan Against Property">Loan Against Property (LAP)</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>
            <div className="relative">
              <select
                data-testid="filter-scope"
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
              >
                <option value="all">All Leads (visible)</option>
                <option value="mine">My Leads</option>
                <option value="team">Team Leads</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Lead #
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Loan Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-muted-foreground">
                      No leads found. Create your first lead to get started.
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className="hover:bg-accent/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono font-medium text-primary">{lead.lead_number || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-foreground">{lead.customer_name}</p>
                          {lead.city && (
                            <p className="text-sm text-muted-foreground">{lead.city}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm text-foreground">{lead.mobile_number}</p>
                          {lead.email && (
                            <p className="text-sm text-muted-foreground">{lead.email}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-foreground capitalize">{lead.loan_type}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-foreground">
                          {lead.loan_amount ? `₹${lead.loan_amount.toLocaleString('en-IN')}` : '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-foreground">{lead.created_by_name || '—'}</p>
                        {lead.created_at && (
                          <p className="text-xs text-muted-foreground">
                            {new Date(lead.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-foreground">{lead.current_owner_name || lead.assigned_to_name || '—'}</p>
                        {lead.current_owner_role && (
                          <p className="text-xs text-muted-foreground capitalize">{(lead.current_owner_role || '').replace(/_/g, ' ')}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-foreground">{lead.branch_name || '—'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(lead.status)}`}>
                          {getStatusLabel(lead.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`view-lead-${lead.id}`}
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            className="p-2 rounded-lg hover:bg-muted transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} className="text-muted-foreground" />
                          </button>
                          {canEdit && (
                            <button
                              data-testid={`edit-lead-${lead.id}`}
                              onClick={() => navigate(`/leads/${lead.id}/edit`)}
                              className="p-2 rounded-lg hover:bg-muted transition-colors"
                              title="Edit Lead"
                            >
                              <Edit2 size={18} className="text-muted-foreground" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              data-testid={`delete-lead-${lead.id}`}
                              onClick={() => handleDelete(lead.id)}
                              className="p-2 rounded-lg hover:bg-destructive/10 transition-colors"
                              title="Delete Lead"
                            >
                              <Trash2 size={18} className="text-destructive" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Leads;
